// CC Carousel: shared grid/carousel behaviour for CC sections (CC Collection Cards, CC Latest Products).
if (!customElements.get('cc-carousel')) {
  customElements.define(
    'cc-carousel',
    class CcCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-cc-carousel-track]');
        if (!this.track) return;

        this.items = Array.from(this.track.querySelectorAll('[data-cc-carousel-item]'));
        this.prevButton = this.querySelector('[data-cc-carousel-prev]');
        this.nextButton = this.querySelector('[data-cc-carousel-next]');
        this.pagesWrapper = this.querySelector('[data-cc-carousel-pages]');
        this.pageCount = 0;
        this.renderedPage = null;
        this.renderedCount = null;
        if (this.pagesWrapper) this.pagesWrapper.innerHTML = '';

        this.onScroll = this.onScroll.bind(this);
        this.update = this.update.bind(this);
        this.onBlockSelect = this.onBlockSelect.bind(this);
        this.onPrev = () => this.goTo(this.currentPage() - 1);
        this.onNext = () => this.goTo(this.currentPage() + 1);

        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        if (this.prevButton) this.prevButton.addEventListener('click', this.onPrev);
        if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.update);
          this.resizeObserver.observe(this.track);
        } else {
          window.addEventListener('resize', this.update);
        }

        document.addEventListener('shopify:block:select', this.onBlockSelect);
        this.update();
      }

      disconnectedCallback() {
        if (this.track) this.track.removeEventListener('scroll', this.onScroll);
        if (this.prevButton) this.prevButton.removeEventListener('click', this.onPrev);
        if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext);
        if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = null;
        if (this.resizeObserver) this.resizeObserver.disconnect();
        window.removeEventListener('resize', this.update);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
      }

      isCarousel() {
        return getComputedStyle(this.track).display === 'flex';
      }

      maxScroll() {
        return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      }

      step() {
        if (!this.items.length) return 1;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        return this.items[0].getBoundingClientRect().width + gap || 1;
      }

      currentPage() {
        if (!this.pageCount) return 0;
        if (this.track.scrollLeft >= this.maxScroll() - 2) return this.pageCount - 1;
        return Math.min(this.pageCount - 1, Math.round(this.track.scrollLeft / this.step()));
      }

      goTo(index) {
        if (!this.pageCount) return;
        const page = Math.max(0, Math.min(index, this.pageCount - 1));
        const left = page === this.pageCount - 1 ? this.maxScroll() : page * this.step();
        this.track.scrollTo({ left, behavior: this.prefersReducedMotion() ? 'auto' : 'smooth' });
      }

      prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      onScroll() {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateState();
        });
      }

      onBlockSelect(event) {
        if (!this.contains(event.target) || !this.isCarousel()) return;
        const index = this.items.indexOf(event.target);
        if (index > -1) this.goTo(index);
      }

      update() {
        const max = this.maxScroll();
        const scrollable = this.isCarousel() && max > 2;

        this.toggleAttribute('data-scrollable', scrollable);
        this.pageCount = scrollable ? Math.ceil(max / this.step() - 0.05) + 1 : 0;
        this.positionArrows();
        this.updateState();
      }

      // Centre the side arrows on the card images.
      positionArrows() {
        const media = this.querySelector('[data-cc-carousel-media]');
        if (!media) return;
        const top = media.getBoundingClientRect().top - this.getBoundingClientRect().top + media.offsetHeight / 2;
        this.style.setProperty('--cc-carousel-arrow-top', `${Math.round(top)}px`);
      }

      updateState() {
        const current = this.currentPage();
        const max = this.maxScroll();

        if (this.prevButton) this.prevButton.disabled = !this.pageCount || this.track.scrollLeft <= 2;
        if (this.nextButton) this.nextButton.disabled = !this.pageCount || this.track.scrollLeft >= max - 2;

        if (current !== this.renderedPage || this.pageCount !== this.renderedCount) {
          this.renderPages(current);
        }
      }

      // Dots (theme slider dots from component-slider.css), one per position.
      renderPages(current) {
        this.renderedPage = current;
        this.renderedCount = this.pageCount;
        if (!this.pagesWrapper) return;

        const hadFocus = this.pagesWrapper.contains(document.activeElement);
        this.pagesWrapper.innerHTML = '';

        for (let i = 0; i < this.pageCount; i++) {
          const isActive = i === current;
          const button = document.createElement('button');
          button.type = 'button';
          button.className = `slider-counter__link slider-counter__link--dots cc-carousel__dot${isActive ? ' slider-counter__link--active' : ''}`;
          button.setAttribute('aria-label', `Slide ${i + 1} of ${this.pageCount}`);
          if (isActive) button.setAttribute('aria-current', 'true');
          button.innerHTML = '<span class="dot"></span>';
          button.addEventListener('click', () => this.goTo(i));
          this.pagesWrapper.appendChild(button);
        }

        if (hadFocus) {
          const active = this.pagesWrapper.querySelector('.slider-counter__link--active');
          if (active) active.focus({ preventScroll: true });
        }
      }
    }
  );
}

